import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Loader2, X } from 'lucide-react'
import { searchFoods } from '../../../lib/foodSearch'
import { cn } from '../../../lib/utils'

// Debounced search-as-you-type. Calls onPick(result) when the user
// taps a row. Caller is responsible for hydrating their form fields
// from the picked result.
export function FoodSearchField({ onPick }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [open, setOpen] = useState(false)
  const abortRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    // Cancel any in-flight request when the query changes.
    if (abortRef.current) abortRef.current.abort()

    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      setSearching(false)
      return undefined
    }

    setSearching(true)
    const controller = new AbortController()
    abortRef.current = controller
    const timer = setTimeout(async () => {
      // Search language is derived from the query itself, not the UI
      // language — typing "Eggs" returns egg matches regardless of UI locale.
      const list = await searchFoods(q, { signal: controller.signal })
      if (controller.signal.aborted) return
      setResults(list)
      setSearching(false)
    }, 400)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query])

  const handlePick = (food) => {
    onPick(food)
    setQuery('')
    setResults([])
    setOpen(false)
  }

  const clear = () => {
    setQuery('')
    setResults([])
    inputRef.current?.focus()
  }

  return (
    <div className="space-y-1.5">
      <label className="block text-xs text-muted-foreground">
        {t('phoenixTracker.meals.search.label')}
      </label>
      <div className="relative">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={t('phoenixTracker.meals.search.placeholder')}
          className="h-11 w-full ps-10 pe-9 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm focus:outline-none"
        />
        {searching && (
          <Loader2 className="absolute end-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
        {!searching && query && (
          <button
            type="button"
            onClick={clear}
            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && query.trim().length >= 2 && (
        <div className={cn(
          'rounded-lg border border-border bg-card max-h-72 overflow-y-auto',
          results.length === 0 && !searching && 'opacity-90'
        )}>
          {results.length === 0 && !searching && (
            <p className="p-3 text-xs text-muted-foreground text-center">
              {t('phoenixTracker.meals.search.noResults')}
            </p>
          )}
          {results.map((food) => (
            <button
              key={food.id}
              type="button"
              onClick={() => handlePick(food)}
              className="w-full text-start p-2.5 flex items-center gap-2.5 hover:bg-accent transition-colors border-b border-border last:border-b-0"
            >
              {food.imageUrl ? (
                <img
                  src={food.imageUrl}
                  alt=""
                  loading="lazy"
                  className="w-10 h-10 rounded-md object-cover bg-muted flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-md bg-muted flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-medium text-foreground truncate"
                  dir="auto"
                >
                  {food.name}
                </p>
                {/* dir=ltr keeps the numeric+unit fragments readable in RTL UI;
                    BiDi would otherwise reorder "159 kcal / 100g" with the brand. */}
                <p
                  className="text-xs text-muted-foreground truncate"
                  dir="ltr"
                >
                  {[food.brand, `${food.kcal} kcal · 100g`]
                    .filter(Boolean).join(' · ')}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground">
        {t('phoenixTracker.meals.search.attribution')}
      </p>
    </div>
  )
}
